import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import classNames from 'classnames'

import AudioFile from '../../../common/components/AudioFile.js'
import {hasOrderForceVerifyPackingPermissionSelector} from '../../../data/me.js'
import successSoundFilePath from '../../../../images/success-01.mp3'
import {forceVerifyLine} from './packPanelActions.js'
import {useSelector} from '../../../store.js'
import Quantity from '../../../common/components/Quantity.js'
import {packPanelFormSelector} from './packPanelSelectors.js'
import {PICK_MODE} from '../../../common/constants/PackingOrderModal.js'
import PopoverImage from '../../../common/components/PopoverImage.js'
import Icon from '../../../common/icons/Icon.js'

export default function PackLine({
  line: {
    path,
    name,
    sku,
    upc,
    location,
    total_on_hand,
    total_available,
    orderedQuantity,
    verifiedQuantity,
    level,
    hasChildren,
    orderLineIDSets,
    image_url,
    ...line
  },
  status,
  showImgsOrderDetail,
  usesInventory,
}) {
  const {mode, warehouseFocusID} = useSelector(packPanelFormSelector)
  const [highlight, setHighlight] = useState(false)
  const [prevVerifiedQuantity, setPrevVerifiedQuantity] =
    useState(verifiedQuantity)
  const refs = useRef({highlightTimeout: null})
  const hasOrderForceVerifyPackingPermission = useSelector(
    hasOrderForceVerifyPackingPermissionSelector,
  )
  const startHighlightAnimation = useCallback(() => {
    // The weird dance here is to make sure that the highlight class gets
    // removed and added again so that the animation starts over from the
    // beginning if it is in the middle of running
    setTimeout(() => {
      setHighlight(false)

      setTimeout(() => {
        setHighlight(true)
      }, 0)

      clearTimeout(refs.current.highlightTimeout)

      refs.current.highlightTimeout = setTimeout(() => {
        setHighlight(false)
      }, 1000)
    }, 0)
  }, [])
  useEffect(() => {
    if (prevVerifiedQuantity < verifiedQuantity) {
      startHighlightAnimation()
    }

    setPrevVerifiedQuantity(verifiedQuantity)
  }, [verifiedQuantity])
  useEffect(() => {
    return () => clearTimeout(refs.current.highlightTimeout)
  }, [])

  const serialNumbers = orderLineIDSets.reduce((prev, {serialNumbers}) => {
    prev.push(...serialNumbers)
    return prev
  }, [])

  const [poh, aoh] = useMemo(() => {
    if (!warehouseFocusID) {
      return [total_on_hand, total_available]
    }

    const warehouse = line[`warehouse__${warehouseFocusID}`]

    return [warehouse?.physical_on_hand, warehouse?.available]
  }, [warehouseFocusID])

  return (
    <tr
      className={classNames('tr--scan-success', {
        in: highlight,
        'tr--parent-sku': level === 0,
        'tr--has-children': level === 0 && hasChildren,
        'tr--child-sku tr--child-sku-verify': level > 0,
      })}
    >
      <td className="table__td">
        <div
          className={classNames('flex wrap--lg-img-popover', {
            'list--parent-sku': level === 0,
            'list--child-sku list--child-sku-verify': level > 0,
          })}
          style={{marginLeft: `${level * 1}rem`}}
        >
          {showImgsOrderDetail && <PopoverImage src={image_url} alt={name} />}
          <dl className="list">
            {(highlight || status === 'exiting') && (
              <AudioFile src={successSoundFilePath} />
            )}
            <dt className="list__title">{name}</dt>
            <dd className="list__item">SKU: {sku}</dd>
            {upc && <dd className="list__item">UPC: {upc}</dd>}
            {!upc && (
              <dd className="list__item">No UPC set for this product</dd>
            )}
            {location && <dd className="list__item">Location: {location}</dd>}
            {serialNumbers.length > 0 && (
              <dd className="list__item">
                Serial Numbers: {serialNumbers.join(', ')}
              </dd>
            )}
          </dl>
        </div>
      </td>
      {mode === PICK_MODE && usesInventory && (
        <>
          <td className="table__td align-center">
            <span className="fs-00">
              <Quantity value={poh} />
            </span>
          </td>
          <td className="table__td align-center">
            <span className="fs-00">
              <Quantity value={aoh} />
            </span>
          </td>
        </>
      )}
      <td className="table__td align-center">
        <strong
          className={classNames('lh-md', {
            'fs-03': level == 0,
            'fs-02': level > 0,
          })}
        >
          {orderedQuantity}
        </strong>
      </td>
      <td className="table__td table__td--verified overflow-hidden relative border-left--light align-center">
        <strong
          className={classNames('lh-md verified-quantity', {
            'fs-03': level == 0,
            'fs-02': level > 0,
          })}
        >
          {status === 'exiting' ? orderedQuantity : verifiedQuantity}
        </strong>
        {hasOrderForceVerifyPackingPermission && (
          <button
            className="btn btn--link btn--force-verify lighter no-underline"
            type="button"
            title="Force-verify that this line item has been packed correctly"
            onClick={() => forceVerifyLine(path)}
          >
            <Icon name="check-circle" className="fs-01 lh-0" />
          </button>
        )}
      </td>
    </tr>
  )
}
